import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import useIsLoggedIn from 'utils/hooks/use_is_logged_in';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import ActionButtons from './action_buttons';

/**
 * Show this dialog when
 * 1. User has a booking, and try to switch to another tenant by clicking tenant switch menu link, type: hasBookingAndSwitch
 * 2. User has a booking, and landed on another tenant, type: hasBookingAndOnAnotherTenant
 * 3. User has logged-in, and try to switch to another tenant by clicking tenant switch menu link, type: hasLoggedInAndSwitch
 * 4. User has logged-in with an active booking, and try to switch to another tenant by clicking tenant switch menu link, type: hasLoggedInWithBookingAndSwitch
 * 5. User has logged-in, and landed on another tenant, type: hasLoggedInAndOnAnotherTenant
 * 6. User has logged-in with an active booking, and landed on another tenant, type: hasLoggedInWithBookingAndOnAnotherTenant
 */
const TenantSwitchWarning = () => {
  const apiPrefix = ecom.craftConfig.api_prefix;
  const isBookingEmpty = useRef(true);
  const isLogin = useRef(false);
  const bookingTenant =
    useSelector((state) => state.ui?.sealink?.bookingTenant) || {};
  const loginTenant = useSelector((state) => state.user?.login_tenant) || {};
  const bookingAtAnotherTenant =
    bookingTenant.site_url && bookingTenant.site_url !== apiPrefix;
  const loginAtAnotherTenant =
    loginTenant.site_url && loginTenant.site_url !== apiPrefix;
  const [dialogType, setDialogType] = useState(null);
  const [show, setShow] = useState(
    bookingAtAnotherTenant || loginAtAnotherTenant
  );
  const [destination, setDestination] = useState(window.location.href);
  const booking = useSelector(getCurrentBooking);

  isBookingEmpty.current = isEmpty(booking.reservations_attributes);
  isLogin.current = useIsLoggedIn();

  useEffect(() => {
    const switchLinks = document.querySelectorAll('#site-switcher-menu a');

    switchLinks.forEach((link) =>
      link.addEventListener('click', (e) => {
        const { href, target } = e.currentTarget;
        // No need to clear booking when visiting external website in a new tab.
        if (target === '_blank') {
          return;
        }
        const gotoSameTenant = window.location.href.includes(href);
        const onReceiptPage =
          window.location.pathname.includes('/checkout/receipt/');

        // Booking will be auto cleared once it reaches the receipt page
        if (
          !gotoSameTenant &&
          (isLogin.current || (!isBookingEmpty.current && !onReceiptPage))
        ) {
          e.preventDefault();
          setShow(true);
          setDestination(href);

          if (isLogin.current) {
            if (isBookingEmpty.current || onReceiptPage) {
              return setDialogType('hasLoggedInAndSwitch');
            }

            if (!isBookingEmpty.current) {
              return setDialogType('hasLoggedInWithBookingAndSwitch');
            }
          } else {
            setDialogType('hasBookingAndSwitch');
          }
        }
      })
    );

    if (bookingAtAnotherTenant) {
      if (loginAtAnotherTenant) {
        setDialogType('hasLoggedInWithBookingAndOnAnotherTenant');
      } else {
        setDialogType('hasBookingAndOnAnotherTenant');
      }
    } else if (loginAtAnotherTenant) {
      setDialogType('hasLoggedInAndOnAnotherTenant');
    }
  }, [bookingAtAnotherTenant, loginAtAnotherTenant]);

  const { siteName } = ecom.craftConfig;
  const bookingTenantName = bookingTenant.site_name || siteName;
  const loginTenantName = loginTenant.site_name;
  const messages = {
    hasBookingAndOnAnotherTenant: `You currently have a booking with SeaLink ${bookingTenantName}.`,
    hasBookingAndSwitch: `You currently have a booking with SeaLink ${bookingTenantName}. Switching between island destinations will clear your current booking.`,
    hasLoggedInAndOnAnotherTenant: `You are currently logged in with SeaLink ${loginTenantName}.`,
    hasLoggedInWithBookingAndOnAnotherTenant: `You are currently logged in with SeaLink ${loginTenantName} and have an active booking.`,
  };
  messages.hasLoggedInAndSwitch = messages.hasLoggedInAndOnAnotherTenant;
  messages.hasLoggedInWithBookingAndSwitch =
    messages.hasLoggedInWithBookingAndOnAnotherTenant;

  return (
    <Modal
      keyboard={false}
      show={show}
      backdrop="static"
      onHide={() => setShow(false)}
    >
      <Modal.Header>
        <Modal.Title>Important message</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        {messages[dialogType]} How would you like to proceed?
      </Modal.Body>
      <Modal.Footer className="pt-0 px-4 border-0">
        <div className="d-flex flex-column gap-2 w-100">
          <ActionButtons
            type={dialogType}
            bookingTenant={bookingTenant}
            loginTenant={loginTenant}
            destination={destination}
            setShow={setShow}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TenantSwitchWarning;
